"use client";

import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchSingleHtmlPreview, FileBrowserApiError, type SingleHtmlPreviewResponse } from "@/api/file-browser";
import { viewRefKey, type ViewRef } from "@/api/views";
import type { AccessTokenGetter } from "@/lib/utils";
import { Button } from "@/components/ui/button";

const HOST_PATTERN = /^[a-z0-9.-]{1,253}$/i;
type DiagnosticKind = "blocked" | "resource" | "script";

function relativePreviewUrl(url: string): string {
  const resolved = new URL(url, window.location.href);
  return resolved.pathname + resolved.search;
}

export function SingleHtmlPreview({ viewRef, getToken, fileName }: {
  viewRef: ViewRef; getToken: AccessTokenGetter; fileName: string;
}) {
  const { t } = useTranslation("chat");
  const viewKey = viewRefKey(viewRef);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [metadata, setMetadata] = useState<SingleHtmlPreviewResponse | null>(null);
  const [retry, setRetry] = useState(0);
  const [unavailable, setUnavailable] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [diagnostics, setDiagnostics] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    setMetadata(null);
    setUnavailable(false);
    setLoadFailed(false);
    setDiagnostics([]);
    let metadataReceived = false;
    void fetchSingleHtmlPreview(viewRef, getToken).then(async (value) => {
      metadataReceived = true;
      // Check the capability response before mounting the frame. A 404 or an
      // expired token otherwise appears as an empty sandboxed document.
      const previewPath = relativePreviewUrl(value.preview_url);
      const response = await fetch(previewPath, { method: "HEAD", credentials: "omit" });
      const csp = response.headers.get("content-security-policy") ?? "";
      if (!response.ok
        || !response.headers.get("content-type")?.toLowerCase().includes("text/html")
        || !csp.includes("connect-src 'none'")
        || !csp.includes("sandbox allow-scripts")
        || csp.includes("allow-same-origin")) {
        throw new Error("HTML preview unavailable");
      }
      if (!cancelled) setMetadata(value);
    }).catch((error: unknown) => {
      if (!cancelled) {
        if (metadataReceived) setLoadFailed(true);
        else if (error instanceof FileBrowserApiError && error.status !== 404) setLoadFailed(true);
        else if (error instanceof TypeError) setLoadFailed(true);
        else setUnavailable(true);
      }
    });
    return () => { cancelled = true; };
  }, [viewKey, getToken, retry]);

  useEffect(() => {
    const receive = (event: MessageEvent) => {
      if (!iframeRef.current || event.source !== iframeRef.current.contentWindow) return;
      const data: unknown = event.data;
      if (!data || typeof data !== "object") return;
      const message = data as Record<string, unknown>;
      if (message.protocol !== "l9-html-preview" || message.version !== 1) return;
      if (!["blocked", "resource", "script"].includes(String(message.kind))) return;
      if (typeof message.host !== "string" || (message.host && !HOST_PATTERN.test(message.host))) return;
      const kind = message.kind as DiagnosticKind;
      const label = kind === "blocked"
        ? t("singleFile.externalBlocked")
        : kind === "resource"
          ? t("singleFile.resourceFailed", { host: message.host || t("viewer.htmlDocument") })
          : t("singleFile.scriptFailed");
      setDiagnostics((previous) => previous.includes(label) ? previous : [...previous.slice(-4), label]);
    };
    window.addEventListener("message", receive);
    return () => window.removeEventListener("message", receive);
  }, [t]);

  return <div className="flex h-full min-h-0 flex-col gap-2" data-testid="single-html-preview">
    <p className="text-xs text-muted-foreground">{t("singleFile.previewRestricted")}</p>
    {metadata?.external_dependencies.map((host) => <p key={host} role="alert" className="text-xs text-amber-700 dark:text-amber-300">
      {t("singleFile.externalDependency", { host })}
    </p>)}
    {diagnostics.length > 0 && <div role="alert" className="text-xs text-amber-700 dark:text-amber-300">
      {diagnostics.map((message) => <p key={message}>{message}</p>)}
    </div>}
    {(unavailable || loadFailed) && <div role="alert" className="flex items-center gap-2 text-sm text-destructive">
      <span>{unavailable ? t("singleFile.previewUnavailable") : t("singleFile.previewLoadFailed")}</span>
      <Button variant="outline" size="sm" onClick={() => setRetry((value) => value + 1)}>{t("singleFile.retryPreview")}</Button>
    </div>}
    {metadata && !loadFailed && <iframe
      key={metadata.preview_url}
      ref={iframeRef}
      src={relativePreviewUrl(metadata.preview_url)}
      sandbox="allow-scripts"
      referrerPolicy="no-referrer"
      title={fileName}
      className="min-h-0 flex-1 w-full border"
      onError={() => setLoadFailed(true)}
    />}
  </div>;
}
